import React, {
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState
} from "react";
import axios from "axios";
import {
    Alert,
    Button,
    Form,
    Modal,
    Spinner
} from "react-bootstrap";
import PropTypes from "prop-types";

import ShopsContext from "../../../../context/ShopsContext";
import Loading from "../../../../components/Loading";
import ProductTypePill from "../../../../components/ProductTypePill";

function DiscountCodeAddProductModal({ show, handleClose, existingProducts, onSave }) {
    const shopsContext = useContext(ShopsContext);
    const [products, setProducts] = useState(null);
    const [filteredProducts, setFilteredProducts] = useState(null);
    const [searchValue, setSearchValue] = useState("");
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [errorSave, setErrorSave] = useState(null);

    const searchInput = useRef(null);
    const activeProductListItem = useRef(null);

    const filterProducts = useCallback((allProducts, search) => {
        if(!allProducts) {
            return allProducts;
        }
        if(!search || search.trim().length === 0) {
            return allProducts;
        }
        const searchParts = search.trim().toLowerCase().split(" ");
        return allProducts.filter((product) => {
            for(const part of searchParts) {
                if(!product.name.toLowerCase().includes(part)) {
                    return false;
                }
            }
            return true;
        });
    }, []);

    const getProducts = useCallback(() => {
        setProducts(null);
        setFilteredProducts(null);
        setError(null);
        axios.post("/getProducts", { shopId: shopsContext.currentShop.id })
            .then((response) => {
                if(response.data.valid) {
                    const loadedProducts = response.data.products;
                    setProducts(loadedProducts);
                    setFilteredProducts(filterProducts(loadedProducts, searchValue));
                } else {
                    setError("Er ging iets fout bij het laden van de producten. (" + response.data.error + ")");
                }
            })
            .catch((requestError) => {
                console.error(requestError);
                setError("Er ging iets fout bij het laden van de producten.");
            });
    }, [shopsContext.currentShop.id, filterProducts, searchValue]);

    const handleArrowKeyPress = useCallback((direction) => {
        setSelectedProduct((prevSelected) => {
            if(!filteredProducts || filteredProducts.length === 0) {
                return prevSelected;
            }
            if(prevSelected == null && direction === "down") {
                if(searchInput.current && searchInput.current === document.activeElement) {
                    searchInput.current.blur();
                }
                return filteredProducts[0].id;
            }
            const index = filteredProducts.findIndex((product) => product.id === prevSelected);
            if(direction === "up") {
                if(index <= 0) {
                    if(searchInput.current) {
                        searchInput.current.focus();
                    }
                    return null;
                }
                return filteredProducts[index - 1].id;
            } else if(direction === "down") {
                if(index + 1 >= filteredProducts.length) {
                    return prevSelected;
                }
                return filteredProducts[index + 1].id;
            }
            return prevSelected;
        });
        setTimeout(() => {
            if(activeProductListItem.current) {
                activeProductListItem.current.scrollIntoView({ block: "center" });
            }
        }, 0);
    }, [filteredProducts]);

    useEffect(() => {
        if(!show) {
            return undefined;
        }
        const onKeyDown = (event) => {
            if(event.keyCode === 38) {
                event.preventDefault();
                handleArrowKeyPress("up");
            } else if(event.keyCode === 40) {
                event.preventDefault();
                handleArrowKeyPress("down");
            }
        };
        document.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [show, handleArrowKeyPress]);

    const onShow = useCallback(() => {
        setSaving(false);
        setSelectedProduct(null);
        setSearchValue("");
        setErrorSave(null);
        if(!products) {
            getProducts();
        } else {
            setFilteredProducts(filterProducts(products, null));
        }
        setTimeout(() => {
            if(searchInput.current) {
                searchInput.current.focus();
            }
        }, 100);
    }, [products, getProducts, filterProducts]);

    useEffect(() => {
        if(products) {
            setFilteredProducts(filterProducts(products, searchValue));
        }
    }, [searchValue, products, filterProducts]);

    const doesProductExistAlready = useCallback((product) => {
        if(!existingProducts || !product) {
            return false;
        }
        return existingProducts.find((ep) => ep.id === product.id) !== undefined;
    }, [existingProducts]);

    const getSelectedProductObject = useCallback(() => {
        if(!products || !selectedProduct) {
            return null;
        }
        return products.find((product) => product.id === selectedProduct);
    }, [products, selectedProduct]);

    const canSubmit = useCallback(() => {
        const selectedProductObj = getSelectedProductObject();
        return selectedProductObj !== null && !doesProductExistAlready(selectedProductObj);
    }, [getSelectedProductObject, doesProductExistAlready]);

    const handleSave = useCallback((event) => {
        if(event) {
            event.preventDefault();
        }
        if(!selectedProduct) {
            setErrorSave("Selecteer een product.");
            return;
        }
        setSaving(true);
        setErrorSave(null);
        onSave(getSelectedProductObject(), () => {
            handleClose();
        }, (saveError) => {
            setSaving(false);
            setErrorSave(saveError);
        });
    }, [selectedProduct, onSave, handleClose]);

    return (
        <Modal show={ show } onHide={ handleClose } onShow={ onShow }>
            <Modal.Header closeButton>
                <Modal.Title>Product toevoegen</Modal.Title>
            </Modal.Header>
            <form className="mb-0" onSubmit={ handleSave }>
                <Modal.Body>
                    { error ? (
                        <Alert variant="danger">{ error }</Alert>
                    ) : !products || !filteredProducts ? (
                        <Loading/>
                    ) : (
                        <React.Fragment>
                            { errorSave && (
                                <Alert variant="danger">{ errorSave }</Alert>
                            )}
                            <Form.Control
                                ref={ searchInput }
                                type="search"
                                value={ searchValue }
                                onChange={ (event) => setSearchValue(event.target.value) }
                                className="mb-3"
                                placeholder="Zoeken"
                                disabled={ saving }
                            />
                            <ul className="list-group list-group-border list-group-scroll" style={{
                                height: "500px"
                            }}>
                                { filteredProducts.map((product) => {
                                    const active = product.id === selectedProduct;
                                    let listGroupItemClassNames = ["list-group-item", "list-group-item-hover", "pointer-cursor"];
                                    if(active) {
                                        listGroupItemClassNames.push("active");
                                    }
                                    return (
                                        <li
                                            key={ product.id }
                                            className={ listGroupItemClassNames.join(" ") }
                                            onClick={ () => setSelectedProduct(product.id) }
                                            ref={ active ? activeProductListItem : undefined }
                                        >
                                            <div className="d-flex align-items-center">
                                                <div className="flex-grow-1">
                                                    { product.name }
                                                </div>
                                                <div>
                                                    <ProductTypePill product={ product } light={ active }/>
                                                </div>
                                            </div>
                                        </li>
                                    )
                                })}
                                { filteredProducts.length === 0 && (
                                    <li className="list-group-item text-center d-flex justify-content-center text-muted flex-column h-100">
                                        <h4><i className="fas fa-search"/></h4>
                                        <p>
                                            Geen producten die matchen met je zoekopdracht.
                                        </p>
                                    </li>
                                )}
                            </ul>
                        </React.Fragment>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    { doesProductExistAlready(getSelectedProductObject()) && (
                        <p className="text-danger mt-0">Dit product is al toegevoegd</p>
                    )}
                    <Button variant="secondary" onClick={ handleClose } disabled={ saving }>
                        Annuleer
                    </Button>
                    <Button variant="primary" onClick={ handleSave } disabled={ saving || !canSubmit() }>
                        { saving && (
                            <Spinner animation="border" variant="light" size="sm" className="mr-2"/>
                        )}
                        Toevoegen
                    </Button>
                </Modal.Footer>
            </form>
        </Modal>
    )
}
DiscountCodeAddProductModal.propTypes = {
    show: PropTypes.bool.isRequired,
    handleClose: PropTypes.func.isRequired,
    existingProducts: PropTypes.array.isRequired,
    onSave: PropTypes.func.isRequired,
}

export default React.memo(DiscountCodeAddProductModal);
